import React from "react";
import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { SegmentedPicker } from "../../molecules/SegmentedPicker";
import { STORY_WORKSPACE_MODES } from "../../registry/storyWorkspaceModes";
import { storyWorkspaceModesCopy as copy } from "../../registry/storyWorkspaceModes.copy";
import { useStoryWorkspaceMode } from "../../../hooks/useStoryWorkspaceMode";

import type { StoryModeSwitchProps } from "./StoryModeSwitch.types";

/** Modes that wait for the story to be built; `locked` and a false `isPresentEnabled` hold them back. */
const LOCKABLE_MODES = ["present", "explore"];

/**
 * A gated mode stays unclickable but reads in the normal unselected text colour, not as disabled grey
 * (F07 #13, F19): the design shows Present like Explore. The picker's own disabled colour is overridden here.
 */
const GatedAsText = styled.span`
  display: inline-flex;
  [role="radio"]:disabled {
    color: ${rawColors.text.mediumEmphasis};
  }
`;

/** Build / Present / Explore, centred in the top bar. Shares its state with the tool bar. */
export const StoryModeSwitch: React.FC<StoryModeSwitchProps> = ({
  isPresentEnabled = true,
  locked = false,
}) => {
  const { mode, setMode } = useStoryWorkspaceMode();
  const options = STORY_WORKSPACE_MODES.map(({ id, label, soon }) => ({
    id,
    label,
    soon,
    // The mode you are in never reads as disabled (F07 #6): it keeps its high-emphasis text.
    disabled:
      id !== mode &&
      ((locked && LOCKABLE_MODES.includes(id)) ||
        (id === "present" && !isPresentEnabled)),
  }));
  return (
    <GatedAsText>
      <SegmentedPicker
        options={options}
        value={mode}
        onChange={(id) => setMode(id as typeof mode)}
        ariaLabel={copy.switchLabel}
        variant="pill"
        slidingThumb
      />
    </GatedAsText>
  );
};

export default StoryModeSwitch;
